import { ApolloClient } from '@apollo/client';
import invariant from 'tiny-invariant';
import _ from 'lodash';
import ConversationMessagesHQQuery from './ConversationMessages.hq.graphql';
import type {
  ConversationMessagesHQ,
  ConversationMessagesHQVariables,
  ConversationMessagesHQ_conversationMessages_messages as Message,
} from './__generated__/ConversationMessagesHQ';

import ConversationsListHQQuery from './ConversationsList.hq.graphql';
import type {
  ConversationsListHQ,
  ConversationsListHQVariables,
} from './__generated__/ConversationsListHQ';

/**
 *
 * messageCompositeKey is a composite of convoUuid and messageUuid,
 * we use it for certain optimistic updates and for cache management
 * of new messages
 */
export function getMessageCompositeKey({
  convoUuid,
  messageUuid,
}: {
  convoUuid: string;
  messageUuid: string;
}): string {
  return `${convoUuid}.${messageUuid}`;
}

/**
 * Mark conversation as read optimistically
 * TODO if search is implemented we will need to update the query we are updating
 * and figure out how it all plays together
 */
export function markAsRead(args: {
  client: ApolloClient<object>;
  convoUuid: string;
  orgUuid: string;
}) {
  args.client.cache.updateQuery<ConversationsListHQ, ConversationsListHQVariables>(
    {
      query: ConversationsListHQQuery,
      overwrite: true,
      variables: {
        input: {
          organization: args.orgUuid,
        },
      },
    },
    (_data) => {
      // if we don't cloneDeep, the update will silently break... freaking apollo
      const data = _.cloneDeep(_data);

      invariant(data, "trying to update a conversation that doesn't exist");
      // update the preview and the lastMessageTime of the right conversation
      const newItems = data.conversationsList.items.map((convo) => {
        if (convo.conversationKey.conversationUuid !== args.convoUuid) {
          return convo;
        }

        return {
          ...convo,
          isUnread: false,
        };
      });

      return {
        conversationsList: {
          ...data.conversationsList,
          items: newItems,
        },
      };
    }
  );
}

/**
 * Insert a new message into the cache so that it appears immediatly
 * in the UI. This is an optimistic update, so it will be replaced
 * by data coming from the server later, the appropriate mechanisms
 * for that to happen are defined in src/lib/apollo/cache/memory.ts
 * and tested in src/conversations/cache/conversationMessages
 *
 */
export function insertMessage(args: {
  client: ApolloClient<object>;
  convoUuid: string;
  compositeKey: string;
  orgUuid: string;
  sequenceId: number;
  currentUserUuid: string;
  text: string;
}) {
  const localMessage: Message = {
    __typename: 'ConversationMessageView',
    sequenceId: args.sequenceId,
    message: {
      __typename: 'MessageView',
      compositeKey: args.compositeKey,
      sentAt: new Date().toISOString(),
      composer: {
        __typename: 'MessageComposerView',
        uuid: args.currentUserUuid,
        // we really don't care about this,
        // because we don't show them and they will be filled up when
        // actual data from the server comes
        name: '',
        avatar: {
          __typename: 'UserInitialsAvatarView',
          kind: '',
          initials: '',
        },
      },
      textBody: {
        __typename: 'TextBodyView',
        text: args.text,
        linkAttributes: [],
      },
      linkPreviews: [],
      deliveryState: {
        __typename: 'DeliveryStateView',
        // TODO
        state: 'sending',
        errorCount: 0,
      },
      translation: null,
    },
  };

  args.client.cache.updateQuery<ConversationMessagesHQ, ConversationMessagesHQVariables>(
    {
      query: ConversationMessagesHQQuery,
      overwrite: true,
      variables: {
        input: {
          conversationKey: {
            organizationUuid: args.orgUuid,
            conversationUuid: args.convoUuid,
          },
        },
      },
    },
    (_data) => {
      // if we don't cloneDeep, the update will silently break... freaking apollo
      const data = _.cloneDeep(_data);
      invariant(data, "trying to update a conversation that doesn't exist");

      return {
        conversationMessages: {
          __typename: 'ConversationMessagesView',
          hasMoreSlices: data?.conversationMessages.hasMoreSlices ?? false,
          messages: [localMessage, ...data.conversationMessages.messages],
        },
      };
    }
  );

  updateConvoPreview({ ...args });
}

export function updateMessageDeliveryState(args: {
  client: ApolloClient<object>;
  orgUuid: string;
  convoUuid: string;
  sequenceId: number;
  deliveryState: string;
}) {
  args.client.cache.updateQuery<ConversationMessagesHQ, ConversationMessagesHQVariables>(
    {
      query: ConversationMessagesHQQuery,
      overwrite: true,
      variables: {
        input: {
          conversationKey: {
            organizationUuid: args.orgUuid,
            conversationUuid: args.convoUuid,
          },
        },
      },
    },
    (_data) => {
      // if we don't cloneDeep, the update will silently break... freaking apollo
      const data = _.cloneDeep(_data);
      invariant(data, "trying to update a conversation that doesn't exist");

      const messages = data.conversationMessages.messages.map((message) => {
        if (message.sequenceId !== args.sequenceId) {
          return message;
        }

        message.message.deliveryState.state = args.deliveryState;

        return message;
      });

      return {
        conversationMessages: {
          ...data.conversationMessages,
          messages: messages,
        },
      };
    }
  );
}

export function removeMessage(args: {
  client: ApolloClient<object>;
  orgUuid: string;
  convoUuid: string;
  sequenceId: number;
}) {
  args.client.cache.updateQuery<ConversationMessagesHQ, ConversationMessagesHQVariables>(
    {
      query: ConversationMessagesHQQuery,
      overwrite: true,
      variables: {
        input: {
          conversationKey: {
            organizationUuid: args.orgUuid,
            conversationUuid: args.convoUuid,
          },
        },
      },
    },
    (_data) => {
      // if we don't cloneDeep, the update will silently break... freaking apollo
      const data = _.cloneDeep(_data);
      invariant(data, "trying to update a conversation that doesn't exist");

      const messages = data.conversationMessages.messages.filter(
        (message) => message.sequenceId !== args.sequenceId
      );

      return {
        conversationMessages: {
          ...data.conversationMessages,
          messages: messages,
        },
      };
    }
  );
}

/**
 * Update the preview in the convoList
 */
export function updateConvoPreview(args: {
  client: ApolloClient<object>;
  convoUuid: string;
  orgUuid: string;
  text: string;
}) {
  args.client.cache.updateQuery<ConversationsListHQ, ConversationsListHQVariables>(
    {
      query: ConversationsListHQQuery,
      overwrite: true,
      variables: {
        input: {
          organization: args.orgUuid,
        },
      },
    },
    (_data) => {
      // if we don't cloneDeep, the update will silently break... freaking apollo
      const data = _.cloneDeep(_data);

      invariant(data, "trying to update a conversation that doesn't exist");
      // update the preview and the lastMessageTime of the right conversation
      const newItems = data.conversationsList.items.map((convo) => {
        if (convo.conversationKey.conversationUuid !== args.convoUuid) {
          return convo;
        }

        return {
          ...convo,
          preview: args.text,
          lastMessageTime: new Date().toISOString(),
        };
      });

      return {
        conversationsList: {
          ...data.conversationsList,
          items: newItems,
        },
      };
    }
  );
}
